// La partition (pur, sans Web Audio) : ce qui se passe dans le jeu → une « situation » → une « palette »
// (mode, tempo, mesure, instruments, rythmes, bruits du monde). Le chef d'orchestre (conductor.ts) joue la palette.
//
// Ce qui compte, du plus fort au plus faible : le moment (accueil, création, combat, donjon, voyage, sommeil,
// agonie, mort, égarement, exploration), le ton du monde, le biome et le type de lieu, l'heure de Paris, la météo.
import type { Biome, ClassId, LocationKind, RoomKind, TonePreset } from "../../shared/types";
import type { AppState } from "../store";
import { sceneAmbience } from "../components/Scene";
import type { ScenePhase } from "../components/Scene";
import type { BedId, MelodicId, PadId, TextureId } from "./instruments";
import { brighter, darker, dice, PROGRESSIONS } from "./theory";
import type { ModeId } from "./theory";

export type Moment =
  | "accueil" | "connexion" | "creation" | "exploration" | "voyage" | "combat" | "donjon" | "sommeil" | "agonie" | "mort" | "egare";

export type Weather = "clair" | "brume" | "pluie" | "neige" | "orage";

export interface Situation {
  moment: Moment;
  tone: TonePreset;
  /** graine du monde (son code) : chaque monde a sa propre musique */
  world: string;
  worldName: string;
  classId: ClassId | null;
  /** nom et emblème du héros : son leitmotiv */
  hero: string | null;
  biome: Biome;
  kind: LocationKind | null;
  safe: boolean;
  danger: number;
  phase: ScenePhase;
  weather: Weather;
  /** points de vie restants (0 à 1) */
  hp: number;
  foes: number;
  /** fin du tour de combat dans … ms (null hors combat) */
  deadlineIn: number | null;
  room: RoomKind | null;
  depth: number;
  companions: { name: string; classId: ClassId | null }[];
  /** le Conteur écrit */
  weaving: boolean;
}

export type BassStyle = "ronde" | "bourdon" | "oompa" | "ostinato" | "marche";
export type PercStyle = "marche" | "danse" | "bataille" | "valse" | "shanty" | "horloge" | "jungle" | "darbouka" | "lent" | "oompa" | "furtif";
export type ArpStyle = "montant" | "vague" | "battu";
export type ThemeSource = "veillee" | "monde" | "heros";

export interface Palette {
  key: string;
  /** tonique de la mélodie (MIDI) */
  root: number;
  mode: ModeId;
  /** temps par minute (l'unité de temps de la mesure : la croche en 6/8) */
  bpm: number;
  beats: 3 | 4 | 6;
  progression: number[];
  barsPerChord: 1 | 2;
  lead: MelodicId | null;
  answer: MelodicId | null;
  /** part de mesures où la mélodie joue (0 à 1) */
  density: number;
  arp: MelodicId | null;
  arpStyle: ArpStyle;
  pad: PadId | null;
  bass: BassStyle | null;
  perc: PercStyle | null;
  sparkle: MelodicId | null;
  heartbeat: boolean;
  tick: boolean;
  weaving: boolean;
  /** 0 à 1 : vélocités, densité des percussions */
  intensity: number;
  reverb: number;
  brightness: number;
  /** passe-bas général (Hz) : l'agonie et l'égarement étouffent le monde */
  muffle: number;
  beds: Partial<Record<BedId, number>>;
  textures: Partial<Record<TextureId, number>>;
  theme: ThemeSource;
  volume: number;
}

// ───────── Situation ─────────

/** Ce que l'écran de création annonce (classe en cours de choix). */
export interface MusicHint { classId?: ClassId | null }

export function situationOf(app: AppState, hint: MusicHint, now: number): Situation {
  const view = app.view;
  const base: Situation = {
    moment: "accueil", tone: "libre", world: "veillee", worldName: "Veillée", classId: null, hero: null,
    biome: "foret", kind: "clairiere", safe: true, danger: 0, phase: sceneAmbience("veillee", "foret", now).phase,
    weather: "clair", hp: 1, foes: 0, deadlineIn: null, room: null, depth: 0, companions: [], weaving: false,
  };
  if (!app.session) return base;
  if (!view) return { ...base, moment: "connexion", world: app.session.code, worldName: app.session.worldName };

  const loc = view.location;
  const amb = sceneAmbience(loc?.id ?? view.world.id, loc?.biome ?? "plaine", now);
  const c = view.character;
  const classes = new Map(view.others.map((o) => [o.id, o.classId] as const));
  const s: Situation = {
    ...base,
    tone: view.world.tone ?? "libre",
    world: view.world.code,
    worldName: view.world.title || view.world.name,
    classId: c?.classId ?? hint.classId ?? null,
    hero: c ? `${c.name}${c.appearance.sigil}` : null,
    biome: loc?.biome ?? "plaine",
    kind: loc?.kind ?? null,
    safe: loc?.safe ?? false,
    danger: loc?.danger ?? 0,
    phase: amb.phase,
    weather: amb.weather,
    hp: c && c.hpMax > 0 ? Math.max(0, Math.min(1, c.hp / c.hpMax)) : 1,
    companions: view.othersHere.map((o) => ({ name: `${o.name}${o.sigil}`, classId: classes.get(o.id) ?? null })),
    weaving: app.gm.busy || view.gmBusy,
  };
  if (!view.me.hasCharacter || !c) return { ...s, moment: "creation" };
  if (c.lifeState === "mort") return { ...s, moment: "mort" };
  if (c.lifeState === "egare") return { ...s, moment: "egare" };
  if (c.lifeState === "agonie") return { ...s, moment: "agonie" };
  const combat = view.combat;
  if (combat && combat.status === "en_cours") {
    return {
      ...s,
      moment: "combat",
      foes: combat.combatants.filter((x) => x.side === "ennemis" && x.hp > 0).length,
      deadlineIn: combat.roundDeadline - now,
    };
  }
  if (c.lifeState === "endormi") return { ...s, moment: "sommeil" };
  if (view.dungeon) return { ...s, moment: "donjon", room: view.dungeon.room.kind, depth: view.dungeon.level };
  if (c.travel || c.lifeState === "en_route") return { ...s, moment: "voyage" };
  return { ...s, moment: "exploration" };
}

// ───────── Palette ─────────

type Base = Pick<Palette, "root" | "mode" | "bpm" | "beats" | "lead" | "answer" | "arp" | "arpStyle" | "pad" | "bass" | "perc" | "sparkle">;

const TONES: Record<Exclude<TonePreset, "libre">, Base> = {
  heroique: { root: 62, mode: "dorien", bpm: 84, beats: 4, lead: "cor", answer: "flute", arp: "harpe", arpStyle: "vague", pad: "cordes", bass: "ronde", perc: "marche", sparkle: null },
  sombre: { root: 64, mode: "eolien", bpm: 60, beats: 4, lead: "violon", answer: "cloche", arp: "harpe", arpStyle: "montant", pad: "choeurOu", bass: "bourdon", perc: "lent", sparkle: "cloche" },
  feerique: { root: 65, mode: "lydien", bpm: 168, beats: 6, lead: "flute", answer: "celesta", arp: "harpe", arpStyle: "vague", pad: "choeurOu", bass: "ronde", perc: null, sparkle: "celesta" },
  humoristique: { root: 60, mode: "ionien", bpm: 116, beats: 4, lead: "basson", answer: "pizz", arp: "pizz", arpStyle: "battu", pad: null, bass: "oompa", perc: "oompa", sparkle: "boite" },
  steampunk: { root: 57, mode: "harmonique", bpm: 132, beats: 3, lead: "accordeon", answer: "boite", arp: "boite", arpStyle: "montant", pad: "orgue", bass: "oompa", perc: "horloge", sparkle: "boite" },
  pirates: { root: 62, mode: "dorien", bpm: 192, beats: 6, lead: "violon", answer: "accordeon", arp: "luth", arpStyle: "battu", pad: null, bass: "ronde", perc: "shanty", sparkle: null },
  post_apo: { root: 59, mode: "eolien", bpm: 66, beats: 4, lead: "guitare", answer: "flute", arp: "guitare", arpStyle: "montant", pad: "brume", bass: "bourdon", perc: "lent", sparkle: "glas" },
};

/** Le ton « libre » : chaque monde tire au sort sa propre couleur, toujours la même pour lui. */
function freeTone(world: string): Base {
  const r = dice(`libre|${world}`);
  const beats = r.pick([3, 4, 6] as const);
  return {
    root: r.pick([57, 59, 60, 62, 64, 65]),
    mode: r.pick(["dorien", "lydien", "mixolydien", "eolien", "phrygien"] as const),
    bpm: beats === 6 ? r.int(150, 190) : r.int(64, 100),
    beats,
    lead: r.pick(["flute", "harpe", "violon", "celesta", "luth", "cor"] as const),
    answer: r.pick(["celesta", "flute", "luth", "cloche"] as const),
    arp: r.pick(["harpe", "luth", "marimba"] as const),
    arpStyle: r.pick(["montant", "vague", "battu"] as const),
    pad: r.pick(["cordes", "choeurOu", "brume"] as const),
    bass: r.pick(["ronde", "bourdon"] as const),
    perc: r.pick(["marche", "danse", null] as const),
    sparkle: r.pick(["celesta", null] as const),
  };
}

const CLASS_VOICES: Record<ClassId, Base> = {
  guerrier: { root: 62, mode: "dorien", bpm: 88, beats: 4, lead: "cor", answer: "violon", arp: null, arpStyle: "vague", pad: "cordes", bass: "marche", perc: "bataille", sparkle: null },
  mage: { root: 65, mode: "lydien", bpm: 72, beats: 4, lead: "celesta", answer: "flute", arp: "harpe", arpStyle: "vague", pad: "choeurOu", bass: "bourdon", perc: null, sparkle: "celesta" },
  rodeur: { root: 67, mode: "mixolydien", bpm: 92, beats: 4, lead: "flute", answer: "luth", arp: "luth", arpStyle: "montant", pad: "cordes", bass: "ronde", perc: "marche", sparkle: null },
  clerc: { root: 60, mode: "ionien", bpm: 66, beats: 3, lead: "cloche", answer: "flute", arp: "harpe", arpStyle: "montant", pad: "choeur", bass: "bourdon", perc: null, sparkle: "celesta" },
  voleur: { root: 64, mode: "phrygien", bpm: 104, beats: 4, lead: "pizz", answer: "basson", arp: "pizz", arpStyle: "battu", pad: null, bass: "ostinato", perc: "furtif", sparkle: null },
  barde: { root: 62, mode: "dorien", bpm: 180, beats: 6, lead: "luth", answer: "violon", arp: "luth", arpStyle: "battu", pad: null, bass: "ronde", perc: "danse", sparkle: null },
};

/** L'instrument de chaque classe quand son leitmotiv passe dans la musique d'un autre. */
export const CLASS_INSTRUMENT: Record<ClassId, MelodicId> = {
  guerrier: "cor", mage: "celesta", rodeur: "flute", clerc: "cloche", voleur: "pizz", barde: "luth",
};

const OUTDOOR_BIOMES = new Set<Biome>(["plaine", "foret", "montagne", "marais", "desert", "cote", "toundra", "volcan", "jungle"]);
const UNDERGROUND = new Set<LocationKind>(["donjon", "grotte", "tombeau"]);
const SETTLED = new Set<LocationKind>(["village", "ville", "hameau", "port"]);
const ICONIC_LEADS = new Set<TonePreset>(["humoristique", "steampunk", "pirates", "post_apo"]);

function progressionFor(mode: ModeId, world: string, salt: string): number[] {
  const options = PROGRESSIONS[mode];
  return [...dice(`prog|${world}|${salt}`).pick(options)];
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function signature(p: Omit<Palette, "key">): string {
  return JSON.stringify([
    p.root, p.mode, Math.round(p.bpm), p.beats, p.progression, p.lead, p.answer, p.arp, p.arpStyle, p.pad, p.bass, p.perc,
    p.sparkle, p.heartbeat, p.tick, p.weaving, Math.round(p.intensity * 10), Math.round(p.muffle / 500), p.theme,
    Object.entries(p.beds).map(([k, v]) => `${k}${Math.round((v ?? 0) * 10)}`),
    Object.entries(p.textures).map(([k, v]) => `${k}${Math.round((v ?? 0) * 10)}`),
  ]);
}

function finish(p: Omit<Palette, "key">): Palette {
  const q = { ...p, intensity: clamp01(p.intensity), reverb: clamp01(p.reverb), brightness: clamp01(p.brightness), density: clamp01(p.density) };
  return { ...q, key: signature(q) };
}

/** La palette musicale d'une situation. */
export function paletteOf(s: Situation): Palette {
  if (s.moment === "accueil" || s.moment === "connexion") return veilleePalette(s);
  if (s.moment === "creation") return creationPalette(s);

  const tone: Base = s.tone === "libre" ? freeTone(s.world) : TONES[s.tone];
  let p: Omit<Palette, "key"> = {
    ...tone,
    progression: progressionFor(tone.mode, s.world, "base"),
    barsPerChord: tone.bpm < 90 && tone.beats !== 6 ? 1 : 2,
    density: 0.75,
    heartbeat: false, tick: false, weaving: s.weaving,
    intensity: 0.45 + s.danger * 0.05,
    reverb: 0.45, brightness: 0.6, muffle: 20000,
    // la radio d'avant la chute grésille toujours quelque part
    beds: s.tone === "post_apo" ? { statique: 0.25 } : {}, textures: {},
    theme: "monde",
    volume: 1,
  };

  switch (s.moment) {
    case "combat": return finish(combat(p, s));
    case "donjon": return finish(dungeon(p, s));
    case "mort":
      return finish({
        ...p, mode: "eolien", progression: progressionFor("eolien", s.world, "requiem"), bpm: 50, beats: 3, barsPerChord: 1,
        lead: "violon", answer: null, density: 0.4, arp: null, pad: "choeur", bass: "bourdon", perc: null, sparkle: "glas",
        intensity: 0.3, reverb: 0.9, theme: "heros", beds: { vent: 0.12 }, textures: {}, weaving: false,
      });
    case "egare":
      return finish({
        ...p, mode: "tonPar", progression: progressionFor("tonPar", s.world, "fantome"), bpm: 52, beats: 4, barsPerChord: 2,
        lead: "celesta", answer: "glas", density: 0.3, arp: null, pad: "brume", bass: null, perc: null, sparkle: "celesta",
        intensity: 0.25, reverb: 1, muffle: 3200, theme: "heros", beds: { vent: 0.32 }, textures: {},
      });
    case "agonie":
      return finish({
        ...p, mode: "eolien", progression: progressionFor("eolien", s.world, "agonie"), bpm: 48, beats: 4, barsPerChord: 2,
        lead: null, answer: "glas", density: 0.2, arp: null, pad: "choeurOu", bass: "bourdon", perc: null, sparkle: null,
        heartbeat: true, intensity: 0.5, reverb: 0.75, muffle: 950, theme: "heros", beds: { vent: 0.15 }, textures: {},
      });
    case "sommeil":
      p = place({
        ...p, mode: brighter(tone.mode === "tonPar" ? "dorien" : tone.mode), bpm: 54, beats: 3, barsPerChord: 1,
        lead: "boite", answer: null, density: 0.6, arp: "harpe", arpStyle: "montant", pad: "brume", bass: null, perc: null,
        sparkle: null, intensity: 0.15, theme: "heros", volume: 0.65,
      }, s, true);
      return finish({ ...p, progression: progressionFor(p.mode, s.world, "berceuse") });
    case "voyage":
      p = place({
        ...p, bpm: tone.beats === 6 ? tone.bpm : Math.max(88, Math.min(104, tone.bpm * 1.1)), bass: "marche",
        perc: tone.perc === "shanty" || tone.perc === "horloge" ? tone.perc : "marche", lead: ICONIC_LEADS.has(s.tone) ? tone.lead : "flute",
        intensity: 0.5,
      }, s, false);
      p.beds = { ...p.beds, vent: Math.max(p.beds.vent ?? 0, 0.12) };
      return finish(daytime(p, s));
    default:
      return finish(daytime(place(p, s, false), s));
  }
}

/** Le lieu : biome et type de lieu. */
function place(p: Omit<Palette, "key">, s: Situation, quiet: boolean): Omit<Palette, "key"> {
  const q = { ...p, beds: { ...p.beds }, textures: { ...p.textures } };
  const day = s.phase === "jour" || s.phase === "aube";
  const iconic = ICONIC_LEADS.has(s.tone);
  switch (s.biome) {
    case "foret":
      q.textures.oiseaux = day ? 0.7 : 0;
      q.textures.hibou = s.phase === "nuit" ? 0.7 : 0;
      q.beds.vent = 0.08;
      if (!iconic && !quiet) q.answer = "flute";
      break;
    case "montagne":
      q.beds.vent = 0.32;
      q.reverb += 0.2;
      if (!iconic && !quiet) q.answer = "cor";
      break;
    case "marais":
      q.textures.grenouilles = s.phase === "nuit" ? 0.9 : 0.4;
      q.mode = darker(q.mode);
      q.bass = quiet ? q.bass : "bourdon";
      break;
    case "desert":
      q.mode = "hijaz";
      q.beds.vent = 0.14;
      if (!quiet) {
        q.lead = iconic ? q.lead : "oud";
        q.arp = "oud";
        q.perc = q.perc === null ? null : "darbouka";
      }
      break;
    case "cote":
      q.beds.vagues = 0.45;
      q.textures.mouettes = day ? 0.6 : 0;
      if (!iconic && !quiet) q.answer = "accordeon";
      break;
    case "toundra":
      q.beds.vent = 0.42;
      q.sparkle = "celesta";
      q.bpm *= 0.86;
      q.density *= 0.8;
      break;
    case "souterrain":
      q.textures.gouttes = 0.6;
      q.beds.grondement = 0.08;
      q.reverb = 0.9;
      q.mode = darker(q.mode);
      break;
    case "volcan":
      q.beds.grondement = 0.4;
      q.mode = q.mode === "lydien" || q.mode === "ionien" ? "mixolydien" : "phrygien";
      if (!quiet && q.perc !== null) q.perc = "lent";
      break;
    case "jungle":
      q.textures.oiseaux = day ? 0.85 : 0.2;
      q.textures.grillons = s.phase === "nuit" ? 0.8 : 0.35;
      if (!quiet) {
        q.perc = "jungle";
        if (!iconic) q.lead = "marimba";
        q.arp = "marimba";
      }
      break;
    case "plaine":
      q.textures.oiseaux = day ? 0.4 : 0;
      q.beds.vent = 0.1;
      break;
  }
  const kind = s.kind;
  if (kind && SETTLED.has(kind)) {
    if (s.phase === "nuit") {
      q.beds.braise = 0.3;
      q.textures.feu = 0.4;
      if (!quiet) {
        q.perc = null;
        q.lead = iconic ? q.lead : "luth";
      }
    } else if (!quiet && q.perc !== "shanty" && q.perc !== "horloge") {
      // la place du village : on danse
      q.perc = "danse";
      q.arp = q.arp === "harpe" ? "luth" : q.arp;
      q.intensity += 0.1;
    }
    if (kind === "port") {
      q.beds.vagues = Math.max(q.beds.vagues ?? 0, 0.3);
      q.textures.mouettes = day ? 0.7 : 0;
    }
  }
  if ((kind === "camp" || kind === "clairiere") && (s.phase === "nuit" || s.phase === "crepuscule")) {
    q.textures.feu = 0.75;
    q.beds.braise = 0.4;
  }
  if (kind === "sanctuaire") {
    q.pad = "choeur";
    q.perc = null;
    q.reverb = 0.85;
    if (!quiet) q.lead = "cloche";
  }
  if (kind === "tombeau" || kind === "ruines") {
    q.pad = "choeurOu";
    q.mode = darker(q.mode);
    q.perc = null;
    q.sparkle = kind === "tombeau" ? "glas" : q.sparkle;
  }
  if (kind === "chateau" && !quiet && !iconic) {
    q.lead = "cor";
    q.perc = q.perc ?? "marche";
  }
  if (kind === "tour") q.sparkle = "celesta";
  if (kind === "lac") {
    q.arp = "harpe";
    q.mode = brighter(q.mode);
    q.beds.vagues = Math.max(q.beds.vagues ?? 0, 0.1);
  }
  if (kind === "col") q.beds.vent = Math.max(q.beds.vent ?? 0, 0.4);
  if (kind && UNDERGROUND.has(kind)) {
    q.textures.gouttes = 0.5;
    q.reverb = Math.max(q.reverb, 0.85);
    q.textures.oiseaux = 0;
  }
  return q;
}

/** L'heure de Paris et la météo. */
function daytime(p: Omit<Palette, "key">, s: Situation): Omit<Palette, "key"> {
  const q = { ...p, beds: { ...p.beds }, textures: { ...p.textures } };
  const iconic = ICONIC_LEADS.has(s.tone);
  const outdoor = OUTDOOR_BIOMES.has(s.biome) && !(s.kind && UNDERGROUND.has(s.kind));
  switch (s.phase) {
    case "aube":
      q.mode = brighter(q.mode);
      q.bpm *= 0.92;
      if (!iconic) q.lead = "flute";
      if (outdoor) q.textures.oiseaux = Math.min(1, (q.textures.oiseaux ?? 0) + 0.4);
      q.intensity -= 0.15;
      q.theme = "monde";
      break;
    case "crepuscule":
      if (!iconic && q.lead !== "oud" && q.lead !== "marimba") q.lead = "cor";
      if (outdoor) q.textures.grillons = Math.max(q.textures.grillons ?? 0, 0.3);
      q.intensity -= 0.1;
      break;
    case "nuit":
      q.mode = q.mode === "phrygien" || q.mode === "tonPar" || q.mode === "hijaz" ? q.mode : darker(q.mode);
      q.bpm *= 0.82;
      q.density *= 0.55;
      q.perc = null;
      q.bass = q.bass === "oompa" || q.bass === "marche" ? "ronde" : q.bass;
      if (q.arp === "pizz" || q.arp === "luth" || q.arp === "guitare" || q.arp === "harpe") q.arpStyle = "montant";
      if (outdoor) q.textures.grillons = Math.max(q.textures.grillons ?? 0, 0.6);
      if (s.tone === "feerique") q.sparkle = "celesta";
      q.intensity -= 0.2;
      q.volume *= 0.85;
      q.theme = "heros";
      break;
    case "jour":
      break;
  }
  if (outdoor) {
    switch (s.weather) {
      case "pluie":
        q.beds.pluie = 0.45;
        q.textures.gouttesPluie = 0.4;
        q.brightness -= 0.2;
        q.density *= 0.85;
        break;
      case "orage":
        q.beds.pluie = 0.7;
        q.beds.vent = (q.beds.vent ?? 0) + 0.25;
        q.textures.tonnerre = 1;
        q.mode = darker(q.mode);
        q.intensity += 0.15;
        q.textures.oiseaux = 0;
        break;
      case "brume":
        q.reverb += 0.25;
        q.pad = "brume";
        q.density *= 0.7;
        break;
      case "neige":
        q.sparkle = "celesta";
        q.beds.vent = (q.beds.vent ?? 0) + 0.15;
        q.bpm *= 0.9;
        q.brightness += 0.1;
        q.textures.oiseaux = 0;
        q.textures.grillons = 0;
        break;
      case "clair":
        break;
    }
  }
  q.progression = progressionFor(q.mode, s.world, `${s.kind ?? "route"}|${s.phase}`);
  return q;
}

function combat(p: Omit<Palette, "key">, s: Situation): Omit<Palette, "key"> {
  const mode = p.mode === "lydien" || p.mode === "ionien" ? "eolien" : p.mode === "tonPar" ? "phrygien" : darker(p.mode);
  const beats = p.beats === 6 ? 6 : 4;
  const urgent = s.deadlineIn !== null && s.deadlineIn < 20_000;
  const lowHp = s.hp < 0.35;
  return {
    ...p,
    mode,
    beats,
    bpm: beats === 6 ? 210 : 124 + Math.min(16, s.foes * 4),
    progression: progressionFor(mode, s.world, "combat"),
    barsPerChord: 1,
    lead: s.tone === "humoristique" ? "basson" : s.tone === "pirates" ? "violon" : s.tone === "steampunk" ? "accordeon" : "cor",
    answer: "violon",
    density: 0.8,
    arp: s.tone === "humoristique" ? "pizz" : null,
    arpStyle: "battu",
    pad: "cordes",
    bass: "ostinato",
    perc: "bataille",
    sparkle: null,
    heartbeat: lowHp,
    tick: urgent,
    intensity: 0.75 + Math.min(0.15, s.foes * 0.04) + (1 - s.hp) * 0.15,
    reverb: 0.25,
    muffle: lowHp ? 5000 : 20000,
    beds: s.biome === "volcan" ? { grondement: 0.3 } : {},
    textures: {},
    theme: "heros",
  };
}

function dungeon(p: Omit<Palette, "key">, s: Situation): Omit<Palette, "key"> {
  const root = p.root - Math.min(6, s.depth * 2);
  const q: Omit<Palette, "key"> = {
    ...p,
    root,
    mode: "phrygien",
    bpm: 56,
    beats: 4,
    barsPerChord: 2,
    lead: "celesta",
    answer: "glas",
    density: 0.35,
    arp: null,
    pad: "choeurOu",
    bass: "bourdon",
    perc: null,
    sparkle: null,
    intensity: 0.4 + s.depth * 0.05,
    reverb: Math.min(1, 0.8 + s.depth * 0.05),
    beds: { grondement: 0.1 + s.depth * 0.05 },
    textures: { gouttes: 0.6 },
    theme: "monde",
  };
  switch (s.room) {
    case "piege":
      Object.assign(q, { bass: "ostinato", tick: true, intensity: 0.65, bpm: 76 });
      break;
    case "enigme":
      // une boîte à musique qui ne tombe jamais juste : la gamme par tons
      Object.assign(q, { mode: "tonPar", lead: "boite", answer: "celesta", density: 0.7, pad: "brume", bass: null });
      break;
    case "tresor":
      Object.assign(q, { mode: "lydien", arp: "harpe", arpStyle: "vague", sparkle: "celesta", lead: "celesta", density: 0.6, pad: "choeur" });
      break;
    case "repos":
      Object.assign(q, { mode: "dorien", lead: "harpe", answer: null, density: 0.5, pad: "brume", bass: null, intensity: 0.2 });
      q.textures = { ...q.textures, feu: 0.5 };
      q.beds = { ...q.beds, braise: 0.3 };
      break;
    case "boss":
      Object.assign(q, { mode: "harmonique", pad: "choeur", perc: "lent", bass: "ostinato", lead: "cor", density: 0.6, intensity: 0.85, bpm: 66 });
      break;
    default:
      break;
  }
  q.progression = progressionFor(q.mode, s.world, `donjon|${s.room ?? ""}`);
  return q;
}

/** Accueil et connexion : le thème « La Veillée » au coin du feu, qui suit lui aussi l'heure de Paris. */
function veilleePalette(s: Situation): Palette {
  const night = s.phase === "nuit" || s.phase === "crepuscule";
  return finish({
    root: 62, mode: "dorien", bpm: s.moment === "connexion" ? 72 : 66, beats: 3, progression: [0, 6, 0, 2], barsPerChord: 1,
    lead: "harpe", answer: "flute", density: 1, arp: s.moment === "connexion" ? "harpe" : null, arpStyle: "montant",
    pad: s.moment === "connexion" ? "brume" : "cordes", bass: "ronde", perc: null, sparkle: s.moment === "connexion" ? "celesta" : null,
    heartbeat: false, tick: false, weaving: false, intensity: 0.4, reverb: 0.55, brightness: 0.55, muffle: 20000,
    beds: { braise: 0.35 }, textures: { feu: 0.65, grillons: night ? 0.35 : 0 },
    theme: "veillee", volume: 0.9,
  });
}

/** Création du héros : chaque classe a sa voix ; avant le choix, le thème de la Veillée. */
function creationPalette(s: Situation): Palette {
  if (!s.classId) return veilleePalette({ ...s, moment: "accueil" });
  const v = CLASS_VOICES[s.classId];
  return finish({
    ...v,
    progression: progressionFor(v.mode, s.world, `classe|${s.classId}`),
    barsPerChord: v.beats === 6 ? 2 : 1,
    density: 0.8, heartbeat: false, tick: false, weaving: false, intensity: 0.55, reverb: 0.5, brightness: 0.6, muffle: 20000,
    beds: s.classId === "rodeur" ? { vent: 0.08 } : { braise: 0.2 },
    textures: s.classId === "rodeur" ? { oiseaux: 0.6 } : { feu: 0.3 },
    theme: "monde", volume: 1,
  });
}
